import { GetApiUserInfoApiResponse, userOrg } from './userOrgApi';

export const injectedUserOrgApi = userOrg.injectEndpoints({
    endpoints: build => ({
        getApiUserInfoWithHeaders: build.query<GetApiUserInfoApiWithHeadersResponse, GetApiUserInfoApiWithHeadersArg>({
            query: () => ({ url: `/api/UserInfo` }),
            transformResponse: (response: GetApiUserInfoApiResponse, meta) => {
                const headers = meta?.response?.headers;
                const headersObj: UserInfoHeaders = {
                    alertmessage: undefined,
                    alertstatus: undefined,
                };
                if (headers && typeof headers.forEach === 'function') {
                    headers.forEach((value, key) => {
                        headersObj[key] = value;
                    });
                }
                headersObj.alertmessage = headersObj.alertmessage ?? undefined;
                headersObj.alertstatus = headersObj.alertstatus ?? undefined;
                return {
                    data: response,
                    headers: headersObj,
                };
            },
        }),
        getApiUserPreferencesParsed: build.query<GetApiUserPreferencesResponse, void>({
            query: () => ({ url: `/api/User/Preferences` }),
            transformResponse: (response: string) => JSON.parse(response),
        }),
        putApiUserPreferences: build.mutation<void, PutApiUserPreferencesArg>({
            query: body => ({
                url: `/api/UserPreferences`,
                method: 'PUT',
                body,
            }),
        }),
    }),
    // overrideExisting: true,
});

export type PaginationOptions = {
    pageIndex: number;
    pageSize: number;
};

export type TableState = {
    pagination?: PaginationOptions;
    searchText?: string;
    columnSearch?: unknown[];
    selectedRowIds?: (string | number)[];
};

type TableName =
    | 'projects'
    | 'customHPCScriptWorkflows'
    | 'workflows'
    | 'uploadDatasets'
    | 'workflowDatasets'
    | 'publicDatasets'
    | 'jobscripts'
    | 'jobscriptVersions'
    | 'locations'
    | 'providers'
    | 'resourceManagement'
    | 'uploadTransferStatuses'
    | 'stageTransferStatuses'
    | 'deleteTransferStatuses';

type AllTableStates = {
    [key in TableName]?: TableState;
};

export type TableOptions = {
    density?: string;
} & AllTableStates;

export type SelectedMetric = { project: string; type: string; chartType: string };

export type PutApiUserPreferencesArg = {
    systemTextJsonJsonElement: {
        firstBuilderStart?: boolean;
        builderProject?: string | null;
    };
};

export type GetApiUserPreferencesResponse = {
    firstBuilderStart?: boolean;
    builderProject?: string | null;
};

export type GetApiUserInfoApiWithHeadersResponse = UserInfoWithHeadersModel;
export type UserInfoWithHeadersModel = { data: GetApiUserInfoApiResponse; headers: UserInfoHeaders };
export type GetApiUserInfoApiWithHeadersArg = void;
export type UserInfoHeaders = {
    alertmessage: string | undefined;
    alertstatus: 'error' | 'info' | 'success' | 'warning' | undefined;
    [key: string]: string | undefined;
};

export const {
    useGetVersionQuery,
    useWorkerDataQuery,
    useGetApiOtherCountryQuery,
    useGetApiOtherStatisticsQuery,
    useGetApiProjectByProjectShortNameUsersQuery,
    useGetProjectQuery,
    usePostApiProjectMutation,
    usePutApiProjectMutation,
    useDeleteApiProjectByProjectShortNameMutation,
    usePutApiProjectByProjectShortNameProjectResourceAndProjectResourceNameMutation,
    useDeleteApiProjectByProjectShortNameProjectResourceAndProjectResourceNameMutation,
    usePutApiProjectByProjectShortNameProjectTagAndProjectTagIdMutation,
    useDeleteApiProjectByProjectShortNameProjectTagAndProjectTagIdMutation,
    useTemplatesInfoQuery,
    useExtendedProjectInfoQuery,
    useGetApiProjectResourceAssignmentQuery,
    usePostApiProjectResourceAssignmentMutation,
    usePutApiProjectResourceAssignmentMutation,
    useGetProjectResourceAssignmentQuery,
    useDeleteApiProjectResourceAssignmentByIdMutation,
    useGetApiProjectResourceQuery,
    usePostApiProjectResourceMutation,
    usePutApiProjectResourceMutation,
    useGetProjectResourceQuery,
    useDeleteApiProjectResourceByIdMutation,
    useGetApiProjectTagQuery,
    usePostApiProjectTagMutation,
    usePutApiProjectTagMutation,
    useGetProjectTagQuery,
    useDeleteApiProjectTagByIdMutation,
    usePostApiPublicHandleByActionLinkMutation,
    useGetApiPublicRequestInfoByRequestIdQuery,
    useGetApiRequestsQuery,
    usePostApiRequestsMutation,
    useGetApiRequestsSentRequestsQuery,
    usePutApiRequestsByRequestIdAcceptMutation,
    usePutApiRequestsByRequestIdRejectMutation,
    useSyncDetailsQuery,
    useProjectsInfoQuery,
    useGetApiUserPreferencesQuery,
    usePutApiUserPreferencesMutation,
    useGetApiUserInfoQuery,
    useGetApiUserInfoExtendedQuery,
    useGetApiUserInfoWithHeadersQuery,
    useGetApiUserPreferencesParsedQuery,
    useValidRolesQuery,
} = injectedUserOrgApi;
